import {
  faChevronLeft,
  faChevronRight,
  faXmark
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { getMonth, getYear, isValid, parse } from 'date-fns'
import { fr } from 'date-fns/locale/fr'
import { forwardRef, useCallback, useMemo, useRef, useState } from 'react'
import DatePicker, {
  type DatePickerProps as DatePickerPropsType,
  type ReactDatePickerCustomHeaderProps,
  registerLocale,
  setDefaultLocale
} from 'react-datepicker'
import {
  input,
  inputExtraSmall,
  inputSmall
} from '~/components/Form/Input/TextInput.css'
import { getFrenchHolidays } from '~/utils/holidays'
import {
  calendarHeader,
  clearButton,
  dateInput,
  dateInputClearable,
  dateInputWrapper,
  headerYear,
  inputPlaceholder,
  monthNavigation,
  monthNavigationDisabled,
  monthTitle,
  monthYearSelect,
  monthYearSelectItem,
  monthYearSelectItemActive
} from './Datepicker.css'

registerLocale('fr', fr)
setDefaultLocale('fr')

type DateInputProps = React.ComponentPropsWithoutRef<'input'> & {
  displayValue: string
  isEditing: boolean
  showTime?: boolean
  placeholder: string
  clearable?: boolean
  onRawChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onInputFocus: () => void
  onInputBlur: () => void
  inputSize?: 'small' | 'extraSmall'
}

const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      onClick,
      onChange: _onChange,
      value: _value,
      onFocus,
      onBlur: libOnBlur,
      className: dpClassName,
      displayValue,
      isEditing,
      showTime,
      placeholder,
      clearable,
      onRawChange,
      onInputFocus,
      onInputBlur,
      inputSize,
      ...inputRest
    },
    ref
  ) => {
    return (
      <input
        ref={ref}
        type="text"
        value={displayValue}
        onChange={onRawChange}
        onFocus={(e) => {
          onFocus?.(e)
          onInputFocus()
        }}
        onBlur={(e) => {
          libOnBlur?.(e)
          onInputBlur()
        }}
        onClick={onClick}
        placeholder={
          isEditing
            ? showTime
              ? 'jj/mm/aaaa hh:mm'
              : 'jj/mm/aaaa'
            : placeholder
        }
        {...inputRest}
        // After the library props: its own `autoComplete` is undefined and would override ours,
        // and the browser's suggestions would cover the calendar popover
        autoComplete="off"
        className={clsx(
          input,
          dateInput,
          clearable && dateInputClearable,
          !displayValue && !isEditing && inputPlaceholder,
          dpClassName,
          inputSize === 'small' && inputSmall,
          inputSize === 'extraSmall' && inputExtraSmall
        )}
      />
    )
  }
)

export type DatepickerProps = Pick<
  DatePickerPropsType,
  'popperPlacement' | 'name'
> & {
  currentDate: Date | null
  onChange: (date: Date | null) => void
  placeholder?: string
  noPast?: boolean
  showTime?: boolean
  selectsStart?: boolean
  selectsEnd?: boolean
  startDate?: Date
  endDate?: Date
  minDate?: Date
  clearable?: boolean
  dateFormatOptions?: Intl.DateTimeFormatOptions
  inputSize?: 'small' | 'extraSmall'
  disabled?: boolean
}

export const Datepicker = ({
  placeholder = 'Sélectionner une date',
  currentDate,
  onChange,
  noPast,
  showTime,
  minDate,
  clearable = true,
  dateFormatOptions,
  inputSize,
  disabled,
  ...rest
}: DatepickerProps) => {
  const years = [...Array(12).keys()].map(
    (i) => new Date().getFullYear() + i - (noPast ? 0 : 5)
  )

  const datePickerRef = useRef<DatePicker>(null)
  const [rawValue, setRawValue] = useState('')
  const [isEditing, setIsEditing] = useState(false)

  const formatDate = useCallback(
    (date: Date) =>
      new Intl.DateTimeFormat(
        'fr-FR',
        dateFormatOptions ?? {
          dateStyle: 'short',
          timeStyle: showTime ? 'short' : undefined
        }
      ).format(date),
    [dateFormatOptions, showTime]
  )

  const displayValue = isEditing
    ? rawValue
    : currentDate
      ? formatDate(currentDate)
      : ''

  const handleRawChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value
      setRawValue(val)
      datePickerRef.current?.setOpen(false, true)
      if (val === '') {
        onChange(null)
        return
      }
      const format = showTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy'
      const parsed = parse(val, format, new Date())
      if (isValid(parsed)) onChange(parsed)
    },
    [onChange, showTime]
  )

  const handleFocus = useCallback(() => {
    setIsEditing(true)
    setRawValue(currentDate ? formatDate(currentDate) : '')
  }, [currentDate, formatDate])

  const handleBlur = useCallback(() => {
    setIsEditing(false)
    setRawValue('')
  }, [])

  const handleDateChange = useCallback(
    (value: Date | null | [Date | null, Date | null]) => {
      const nextDate = Array.isArray(value) ? value[0] : value
      onChange(nextDate)
      if (!showTime) {
        datePickerRef.current?.setOpen(false)
      }
    },
    [onChange, showTime]
  )

  const CustomHeader = ({
    monthDate,
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled
  }: ReactDatePickerCustomHeaderProps) => {
    const [isMonthSelectOpen, setIsMonthSelectOpen] = useState(false)
    const [isYearSelectOpen, setIsYearSelectOpen] = useState(false)
    return (
      <div className={calendarHeader}>
        <button
          aria-label="Previous Month"
          className={clsx(
            monthNavigation,
            prevMonthButtonDisabled ? monthNavigationDisabled : null
          )}
          onClick={decreaseMonth}
          type="button"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <span className={monthTitle}>
          <span onClick={() => setIsMonthSelectOpen(!isMonthSelectOpen)}>
            {monthDate.toLocaleString('fr-FR', {
              month: 'long'
            })}
          </span>
          <span
            className={headerYear}
            onClick={() => setIsYearSelectOpen(!isYearSelectOpen)}
          >
            {' '}
            {getYear(date)}
          </span>
          {isMonthSelectOpen && (
            <div className={monthYearSelect}>
              {[...Array(12).keys()].map((month) => (
                <span
                  key={month}
                  onClick={() => {
                    changeMonth(month)
                    setIsMonthSelectOpen(false)
                  }}
                  className={clsx(
                    monthYearSelectItem,
                    month === getMonth(date) ? monthYearSelectItemActive : null
                  )}
                >
                  {new Intl.DateTimeFormat('fr-FR', {
                    month: 'long'
                  }).format(new Date(0, month))}
                </span>
              ))}
            </div>
          )}

          {isYearSelectOpen && (
            <div className={monthYearSelect}>
              {years.map((year) => (
                <span
                  key={year}
                  onClick={() => {
                    changeYear(year)
                    setIsYearSelectOpen(false)
                  }}
                  className={clsx(
                    monthYearSelectItem,
                    year === getYear(date) ? monthYearSelectItemActive : null
                  )}
                >
                  {year}
                </span>
              ))}
            </div>
          )}
        </span>
        <button
          aria-label="Mois suivant"
          className={clsx(
            monthNavigation,
            nextMonthButtonDisabled ? monthNavigationDisabled : null
          )}
          onClick={increaseMonth}
          type="button"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
    )
  }

  const filterPassedTime = useCallback(
    (time: Date) => {
      if (minDate) {
        minDate.setSeconds(0)
        minDate.setMilliseconds(0)
        return time > minDate
      }
      if (noPast) {
        return time > new Date()
      }
      return true
    },
    [noPast, minDate]
  )

  const filterPassedDate = useCallback(
    (date: Date) => {
      const now = new Date()
      now.setHours(0, 0, 0, 0)
      if (noPast) {
        return date >= now
      }
      return true
    },
    [noPast]
  )

  const holidays = useMemo(
    () => years.flatMap((year) => getFrenchHolidays(year)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [years[0]]
  )

  return (
    <div className={dateInputWrapper}>
      <DatePicker
        ref={datePickerRef}
        selected={currentDate}
        onChange={handleDateChange}
        dateFormat={showTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy'}
        showTimeSelect={showTime}
        filterDate={filterPassedDate}
        filterTime={filterPassedTime}
        timeIntervals={15}
        timeFormat={showTime ? 'HH:mm' : undefined}
        portalId="appWrapper"
        customInput={
          <DateInput
            displayValue={displayValue}
            isEditing={isEditing}
            showTime={showTime}
            placeholder={placeholder}
            clearable={clearable}
            inputSize={inputSize}
            disabled={disabled}
            onRawChange={handleRawChange}
            onInputFocus={handleFocus}
            onInputBlur={handleBlur}
          />
        }
        disabled={disabled}
        formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 1).toUpperCase()}
        renderCustomHeader={(props) => <CustomHeader {...props} />}
        minDate={minDate}
        highlightDates={[{ 'react-datepicker__day--holiday': holidays }]}
        {...rest}
      />
      {clearable && currentDate && (
        <button
          className={clearButton}
          type="button"
          aria-label="Effacer la date"
          onClick={(e) => {
            e.stopPropagation()
            onChange(null)
          }}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      )}
    </div>
  )
}
